import { useState } from 'react';
import { toast } from 'sonner';
import { GraduationCap, Loader2, Send } from 'lucide-react';
import { Textarea } from '../ui/textarea';

/**
 * "Message the reader" — a short note explaining a correction (e.g. why a job was
 * reassigned). It feeds this supplier's memory so future reads apply the rule.
 */
export function TeachReaderPanel({ api, itemId, supplierName, disabled = false }) {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const send = async () => {
    const text = note.trim();
    if (text.length < 3) return;
    setBusy(true);
    try {
      await api.post(`/cost-inbox/${itemId}/teach`, { note: text, supplier_name: supplierName || undefined });
      toast.success('Thanks — the reader will remember this for next time');
      setNote('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save the note');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-lg border border-white/10 bg-white/[0.03] p-2.5" data-testid="inbox-teach-panel">
      <div className="flex items-center gap-2 mb-1.5">
        <GraduationCap className="w-4 h-4 text-[#F5A623] shrink-0" />
        <div>
          <div className="text-xs text-gray-300 font-medium">Teach the reader</div>
          <div className="text-[11px] text-gray-500">Fixed a job the AI got wrong? Tell it why — it learns this supplier for next time.</div>
        </div>
      </div>
      <Textarea
        value={note}
        onChange={e => setNote(e.target.value)}
        disabled={disabled || busy}
        rows={2}
        placeholder="e.g. Orders quoting &lsquo;Camphill&rsquo; are the Harksome job (LUX Dev) — assign there, not Chapel Lane."
        className="bg-[#1E2530] border-white/20 text-white text-sm resize-none"
        data-testid="inbox-teach-input"
      />
      <div className="flex justify-end mt-1.5">
        <button
          type="button"
          onClick={send}
          disabled={disabled || busy || note.trim().length < 3}
          className="inline-flex items-center gap-1.5 text-xs px-3 h-8 rounded-md bg-[#F5A623] text-[#1E2530] font-medium disabled:opacity-50 hover:bg-[#D98E1D] transition-colors"
          data-testid="inbox-teach-save"
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Teach the reader
        </button>
      </div>
    </div>
  );
}
